import React, { useState, useEffect } from 'react';
import { Button, message, Popconfirm, Space, Table, TableColumnsType, Tag, Modal, Statistic } from 'antd';
import dayjs from 'dayjs';
import {
  getAuthList,
  cancelAuth,
  getUsageStatsList,
  getTodayOverview,
  AgentTenantAuthVo,
  AgentUsageStatsVo,
  StatsOverviewVo,
} from '@/api/tenantAuth';
import { getAgentConfigList, AgentConfigVo } from '@/api/agent';
import { getTenantList, TenantListVo } from '@/api/auth';
import { useUpdate } from '@/common/hook';
import EditAuthModal from './editAuthModal';
import BatchAuthModal from './batchAuthModal';
import { PlusOutlined, TeamOutlined, BarChartOutlined } from '@ant-design/icons';

const TenantAuthList = () => {
  const [update, updateFn] = useUpdate();
  const [agents, setAgents] = useState<AgentConfigVo[]>([]);
  const [selectedAgentId, setSelectedAgentId] = useState<number>(0);
  const [tenantList, setTenantList] = useState<TenantListVo[]>([]);

  // 租户名称映射
  const tenantNameMap = new Map(tenantList.map(t => [t.tenantMcid, t.name]));
  const getTenantName = (mcid: string) => tenantNameMap.get(mcid) || mcid;

  // 授权相关状态
  const [authList, setAuthList] = useState<AgentTenantAuthVo[]>([]);
  const [editVisible, setEditVisible] = useState<boolean>(false);
  const [editData, setEditData] = useState<AgentTenantAuthVo>();
  const [batchVisible, setBatchVisible] = useState<boolean>(false);

  // 统计相关状态
  const [statsVisible, setStatsVisible] = useState<boolean>(false);
  const [statsList, setStatsList] = useState<AgentUsageStatsVo[]>([]);
  const [todayOverview, setTodayOverview] = useState<StatsOverviewVo>();

  const statsColumns: TableColumnsType<AgentUsageStatsVo> = [
    { title: '日期', dataIndex: 'statDate', key: 'statDate' },
    { title: '租户', dataIndex: 'tenantMcid', key: 'tenantMcid', render: mcid => getTenantName(mcid) },
    { title: '总调用', dataIndex: 'totalCalls', key: 'totalCalls' },
    { title: '成功', dataIndex: 'successCalls', key: 'successCalls' },
    { title: '失败', dataIndex: 'failCalls', key: 'failCalls' },
    { title: 'Token消耗', dataIndex: 'totalTokens', key: 'totalTokens' },
    { title: '平均延迟', dataIndex: 'avgLatencyMs', key: 'avgLatencyMs', render: v => v ? `${v}ms` : '-' },
  ];

  useEffect(() => {
    getAgentConfigList().then(list => {
      setAgents(list || []);
      if (list && list.length > 0) {
        setSelectedAgentId(list[0].id);
      }
    });
    getTenantList().then(list => setTenantList(list || []));
  }, []);

  useEffect(() => {
    if (selectedAgentId) {
      loadAuthList();
    }
  }, [selectedAgentId, update]);

  const loadAuthList = () => {
    getAuthList({ agentId: selectedAgentId }).then(list => setAuthList(list || []));
  };

  const handleCancelAuth = (tenantMcid: string) => {
    cancelAuth({ agentId: selectedAgentId, tenantMcid }).then(() => {
      message.success('取消授权成功');
      loadAuthList();
    });
  };

  const handleViewStats = (tenantMcid?: string) => {
    if (!selectedAgentId) {
      message.warning('请先选择智能体');
      return;
    }
    setStatsVisible(true);
    setStatsList([]);
    setTodayOverview(undefined);

    getUsageStatsList({ agentId: selectedAgentId, tenantMcid })
      .then(list => setStatsList(list || []))
      .catch(err => {
        console.error('获取统计数据失败:', err);
        message.error('获取统计数据失败');
      });

    getTodayOverview({ agentId: selectedAgentId, tenantMcid })
      .then(overview => setTodayOverview(overview))
      .catch(err => {
        console.error('获取今日概览失败:', err);
      });
  };

  const authColumns: TableColumnsType<AgentTenantAuthVo> = [
    { title: '租户', dataIndex: 'tenantMcid', key: 'tenantMcid', render: mcid => getTenantName(mcid) },
    { title: '每日限制', dataIndex: 'dailyLimit', key: 'dailyLimit', render: v => v || '无限制' },
    { title: '每月限制', dataIndex: 'monthlyLimit', key: 'monthlyLimit', render: v => v || '无限制' },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: number) => <Tag color={status === 1 ? 'green' : 'red'}>{status === 1 ? '已授权' : '已取消'}</Tag>,
    },
    { title: '授权人', dataIndex: 'authorizedBy', key: 'authorizedBy' },
    {
      title: '授权时间',
      dataIndex: 'authorizedAt',
      key: 'authorizedAt',
      render: text => text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
    { title: '更新人', dataIndex: 'updatedBy', key: 'updatedBy', render: v => v || '-' },
    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: text => text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
  ];

  const authActionColumns = [
    ...authColumns,
    {
      title: '操作',
      key: 'operation',
      render: (_: any, record: AgentTenantAuthVo) => (
        <Space size={4}>
          <Button
            type="link"
            size="small"
            style={{ padding: 0 }}
            onClick={() => {
              setEditData(record);
              setEditVisible(true);
            }}
          >
            修改
          </Button>
          <Button
            type="link"
            size="small"
            style={{ padding: 0 }}
            onClick={() => handleViewStats(record.tenantMcid)}
          >
            统计
          </Button>
          <Popconfirm title="确定取消授权？" onConfirm={() => handleCancelAuth(record.tenantMcid)}>
            <Button type="link" size="small" style={{ padding: 0 }} danger>
              取消授权
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="flex gap-3 mb-3 items-center">
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditVisible(true)}>
          新增授权
        </Button>
        <Button icon={<TeamOutlined />} onClick={() => setBatchVisible(true)}>
          批量授权
        </Button>
        <Button icon={<BarChartOutlined />} onClick={() => handleViewStats()}>
          查看总体统计
        </Button>
        <Space>
          <label>选择智能体：</label>
          <select
            value={selectedAgentId}
            onChange={e => setSelectedAgentId(Number(e.target.value))}
            style={{ width: 200, height: 32 }}
          >
            {agents.map(a => (
              <option key={a.id} value={a.id}>{a.name} ({a.agentCode})</option>
            ))}
          </select>
        </Space>
      </div>
      <Table<AgentTenantAuthVo>
        columns={authActionColumns}
        dataSource={authList}
        rowKey="id"
      />
      {editVisible && (
        <EditAuthModal
          agentId={selectedAgentId}
          data={editData}
          isEdit={!!editData}
          updateFn={updateFn}
          onCancel={() => {
            setEditVisible(false);
            setEditData(undefined);
          }}
        />
      )}
      {batchVisible && (
        <BatchAuthModal
          agentId={selectedAgentId}
          updateFn={updateFn}
          onCancel={() => setBatchVisible(false)}
        />
      )}
      {statsVisible && (
        <Modal
          title="使用统计"
          open={true}
          onCancel={() => setStatsVisible(false)}
          footer={null}
          width={800}
        >
          {todayOverview && (
            <div style={{ marginBottom: 16, padding: 16, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
              <Statistic.Group>
                <Statistic title="今日总调用" value={todayOverview.totalCalls} />
                <Statistic title="成功率" value={todayOverview.successRate} suffix="%" precision={1} />
                <Statistic title="平均延迟" value={todayOverview.avgLatency} suffix="ms" />
                <Statistic title="Token消耗" value={todayOverview.totalTokens} />
              </Statistic.Group>
            </div>
          )}
          {!todayOverview && (
            <div style={{ marginBottom: 16, padding: 16, backgroundColor: '#f5f5f5', borderRadius: 4, textAlign: 'center', color: '#999' }}>
              今日暂无调用数据
            </div>
          )}
          <Table<AgentUsageStatsVo>
            columns={statsColumns}
            dataSource={statsList}
            rowKey="id"
            size="small"
            locale={{ emptyText: '暂无统计数据' }}
          />
        </Modal>
      )}
    </>
  );
};

export default TenantAuthList;